import { computed, set, observable, action, when, observe } from 'mobx';
import { routerStore } from 'core/routing';
import { AppTreeBase, AppRef } from 'core/app';
import { Variables } from 'core/query';
import { fromLocalStorage, toLocalStorage } from 'core/localStorage';

export class AppManager extends AppTreeBase {
  static registryName = 'AppManager';
  @observable _variables = null;
  queries = observable.map();
  @observable mutations = [];
  disposed = false;
  routeName = null;
  autoDisposer = null;
  models = observable.map();
  localApps = observable.map();

  constructor(parent, initial = {}, { autoDispose = true } = {}) {
    super(parent);
    this.dispose = this.dispose.bind(this);
    set(this, initial);
    if (this.parent) {
      const disposers = this.parent?.disposers;
      if (disposers) disposers.push(this.dispose);
      else
        console.error(
          'Parent passed to AppManager ',
          this.constructor.name,
          ' has no disposers property',
        );
    }
    // extendObservable(this, initial);
    this.variables = new Variables();
    if (autoDispose) {
      this.autoDispose();
    }
    this.disposeDisusedQueries();
  }

  disposeDisusedQueries() {
    /* Observe the queries map. If a key is overwritten dispose the old queries */
    this.disposers.push(
      observe(this.queries, change => {
        if (change.type === 'update' || change.type === 'delete') {
          change.oldValue.disposer();
        }
      }),
    );
  }

  // FIXME - alter this approach (?)
  // Instead of creating appmanagers from React components, create them upfront
  // At least, if their creation if requested from React components, still store references to them
  // accesible from a root store and independant from the "React component tree"
  disposeWhen() {
    /* By default dispose appManagers when the route name changes
    Override this on sub class for custom behaviour */
    return routerStore.route.name !== this.routeName;
  }

  autoDispose() {
    const routeObj = routerStore.route;
    if (routeObj !== null) {
      // prior to initialisation
      this.routeName = routeObj.name;
      this.autoDisposer = when(this.disposeWhen.bind(this), () =>
        this.dispose(),
      );
    }
  }

  disableAutoDispose() {
    if (this.autoDisposer) {
      this.autoDisposer();
    }
    this.autoDisposer = null;
  }

  AppRef(propName, AppCls) {
    AppRef(this, propName, AppCls);
  }

  @action SetModel = (propName, modelInst) => {
    this[propName] = modelInst;
  };

  @computed get variables() {
    return this._variables;
  }

  set variables(local) {
    if (!this.manager || !this.manager.variables) {
      this._variables = local;
    } else {
      this._variables = this.manager.variables.extend(local);
    }
  }

  dispose() {
    if (this.autoDisposer) {
      this.autoDisposer();
    }
    if (this.disposed) {
      return;
    }
    if (this.disposer) this.disposer();
    // eslint-disable-next-line no-restricted-syntax
    for (const query of this.queries.values()) {
      if (query.disposer) {
        query.disposer();
      }
    }
    if (this.variables) {
      if (this.variables.dispose) {
        this.variables.dispose();
      }
    }
    this.disposers.forEach(disposer => {
      disposer();
    });
    this.disposed = true;
  }

  fromLocalStorage(key, Model) {
    /* Data from LocalStorage stays in cache as long as its AppManager */
    if (Object.getPrototypeOf(Model).registryName === 'App') {
      // eslint-disable-next-line
      Model = Model.Model;
    }
    const json = fromLocalStorage(key);
    if (json === null) {
      return null;
    }
    return Model.Load(json);
  }

  toLocalStorage(key, obj) {
    toLocalStorage(key, obj);
  }
}
